import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import RegionCountryFilter from './RegionCountryFilter/RegionAndCountryFilters';
import CTFilters from './ExecutionAndTaxonomyFilters/ExecutionAndTaxonomyFilters';
import { parseFilterToApiCall } from '../../services/util';
import { ProjectProductTypes } from '../../models/ProjectProductTypes';
import useFilters from '../../hooks/useFilters';
import AttributesFilter from './AttributesFilter/AttributesFilter';
import { CalledFrom } from '../../models/CalledFromEnum';
import SectorsAndDivisionsFilter from './SectorsAndDivisionsFilter/SectorAndDivisionFilters';
import './Filter.scss';
import TeamLeaderFilter from './TeamLeaderFilter/TeamLeaderFilter';
import PmrFilter from './PmrFilter/PmrFilter';
import TimeWithoutDisbursementsFilter from '../Filter/TimeWithoutDisbursementsFilter/TimeWithoutDisbursementsFilter';
import ProjectAgeFilter from '../Filter/ProjectAgeFilter/ProjectAgeFilter';
import ExcludeRegionalsFilter from '../Filter/ExcludeRegionalsFilter/ExcludeRegionalsFilter';
import { DisbursementDataFilter } from './DisbursementDataFilter/DisbursementDataFilter';
import DisbursementProjectionFilter from '../Filter/DisbursementProjectionFilter/DisbursementProjectionFilter';
import { IndicatorKeyEnum } from '../../models/IndicatorKeyEnum';
import { useSelector } from 'react-redux';
import CustomSelectSmall from '../Util/CustomSelectSmall/CustomSelectSmall';
import { KnowledgeProductsYearFilterContext } from '../../context/KnowledgeProductsYearFilterContext';

type Props = {
  indicatorCall: boolean;
  calledFrom: CalledFrom;
  indicatorKey?: string;
};

const Filters = (props: Props) => {
  const { t } = useTranslation();
  const {
    selectedYear,
    setSelectedYear,
    availableYears,
    selectedProductTypeId,
    setSelectedProductTypeId,
    availableProductTypes,
    selectedPreparationYear,
    setSelectedPreparationYear,
    availablePreparationYears,
    selectedPipelineId,
    setSelectedPipelineId,
    availablePipelines,
  } = useContext(KnowledgeProductsYearFilterContext);

  const { indicatorCall, calledFrom, indicatorKey } = props;
  const { filterIndicators, filter } = useFilters();
  const isLoading = useSelector<any, any>(state => state.dataTableInfo.loading);
  const yearsOptions = [{ value: 0, label: t('all') }, ...availableYears.map(year => ({ value: year, label: year }))];
  const productTypeOptions = [
    { value: 0, label: t('all') },
    ...availableProductTypes.map(productType => ({ value: productType.id, label: productType.label })),
  ];
  const preparationYearsOptions = [
    { value: 0, label: t('all') },
    ...availablePreparationYears.map(year => ({ value: year, label: year })),
  ];
  const pipelineOptions = [
    { value: 0, label: t('all') },
    ...availablePipelines.map(pipeline => ({ value: pipeline.id, label: pipeline.label })),
  ];

  const showFilters = (typeSelected: number): boolean => {
    const projectTypeFilters = indicatorCall
      ? parseFilterToApiCall(filterIndicators.common.projectTypeFilters).projectProductTypes
      : parseFilterToApiCall(filter.common.projectTypeFilters).projectProductTypes;

    return (
      projectTypeFilters.filter(i => i !== ProjectProductTypes.GUARANTEE).length === 1 &&
      projectTypeFilters.includes(typeSelected)
    );
  };

  return (
    <div className={`filter__component__filters ${isLoading ? 'disabled' : ''}`}>
      <RegionCountryFilter calledFrom={calledFrom} />

      {indicatorKey != IndicatorKeyEnum.PROJECTS_KNOWLEDGE_PRODUCTS && (
        <SectorsAndDivisionsFilter calledFrom={calledFrom} />
      )}

      {(showFilters(ProjectProductTypes.Loan) || calledFrom === CalledFrom.EARLY_WARNINGS) && (
        <div className="filter-wrapper">
          <h4 className="paragraph-x-small filter-wrapper__label">{t('pmr_initials')}</h4>
          <PmrFilter indicatorCall={indicatorCall} calledFrom={calledFrom} />
        </div>
      )}

      {indicatorKey != IndicatorKeyEnum.PROJECTS_KNOWLEDGE_PRODUCTS && (
        <>
          <TeamLeaderFilter calledFrom={calledFrom} isEdit />
          <div className="filter-wrapper">
            <h4 className="paragraph-x-small filter-wrapper__label">{t('attributes')}</h4>
            <AttributesFilter calledFrom={calledFrom} />
          </div>
        </>
      )}

      {calledFrom !== CalledFrom.EARLY_WARNINGS && showFilters(ProjectProductTypes.CT) && (
        <CTFilters indicatorCall={indicatorCall} />
      )}

      {calledFrom === CalledFrom.PROJECT && (
        <>
          <div className='filter-wrapper'>
            <h4 className="paragraph-x-small filter-wrapper__label">
              {`${t('time_without_performing_disbursements').trim()} (${t('days').trim()})`}
            </h4>
            <TimeWithoutDisbursementsFilter indicatorCall={indicatorCall} calledFrom={calledFrom} />
          </div>
          <div className='filter-wrapper'>
            <h4 className="paragraph-x-small filter-wrapper__label">{t('project_age')}</h4>
            <ProjectAgeFilter indicatorCall={indicatorCall} calledFrom={calledFrom} />
          </div>
          <div className='filter-wrapper' style={{ display: 'flex', flexDirection: 'column' }}>
            <h4 className="paragraph-x-small filter-wrapper__label">{t('disbursement')}</h4>
            <DisbursementDataFilter />
          </div>
          <div className='filter-wrapper' style={{ display: 'flex', alignItems: 'center' }}>
            <ExcludeRegionalsFilter indicatorCall={indicatorCall} calledFrom={calledFrom} />
          </div>
        </>
      )}

      {calledFrom === CalledFrom.EARLY_WARNINGS && (
        <>
          <div className='filter-wrapper'>
            <h4 className="paragraph-x-small filter-wrapper__label">{t('project_age')}</h4>
            <ProjectAgeFilter indicatorCall={indicatorCall} calledFrom={calledFrom} />
          </div>
          <div className='filter-wrapper'>
            <h4 className="paragraph-x-small filter-wrapper__label">
              {`${t('time_without_performing_disbursements').trim()} (${t('days').trim()})`}
            </h4>
            <TimeWithoutDisbursementsFilter indicatorCall={indicatorCall} calledFrom={calledFrom} />
          </div>
          <div className='filter-wrapper'>
            <h4>{t('projection_disbursed')} (USD)</h4>
            <DisbursementProjectionFilter indicatorCall={indicatorCall} calledFrom={calledFrom} />
          </div>
          <div className='filter-wrapper' style={{ display: 'flex', alignItems: 'center' }}>
            <ExcludeRegionalsFilter indicatorCall={indicatorCall} calledFrom={calledFrom} />
          </div>
        </>
      )}

      {indicatorKey === IndicatorKeyEnum.PROJECTS_KNOWLEDGE_PRODUCTS && (
        <>
          <div className='filter-wrapper'>
            <CustomSelectSmall
              selectValue={selectedYear}
              handleChange={value => setSelectedYear(parseInt(value, 10) || 0)}
              label={t('year')}
              options={yearsOptions}
            />
          </div>
          <div className='filter-wrapper'>
            <CustomSelectSmall
              selectValue={selectedProductTypeId}
              handleChange={value => setSelectedProductTypeId(parseInt(value, 10) || 0)}
              label={t('type_of_product')}
              options={productTypeOptions}
            />
          </div>
        </>
      )}

      {indicatorKey === IndicatorKeyEnum.PROJECTS_IN_PREPARATION && (
        <>
          <div className='filter-wrapper'>
            <CustomSelectSmall
              selectValue={selectedPreparationYear}
              handleChange={value => setSelectedPreparationYear(parseInt(value, 10) || 0)}
              label={t('year')}
              options={preparationYearsOptions}
            />
          </div>
          <div className='filter-wrapper'>
            <CustomSelectSmall
              selectValue={selectedPipelineId}
              handleChange={value => setSelectedPipelineId(parseInt(value, 10) || 0)}
              label={t('Pipeline')}
              options={pipelineOptions}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default Filters;
